/**
 * MKPlayer Error Codes.
 *
 * The following error codes are to be expected from MKPlayer when an error occurs. Details about the error codes and its message with reason is as detailed below.
 *
 * The error codes are divided into three categories:
 *
 * - MediaKind TV Platform (TVP) Backend errors
 * - Player errors
 * - Program entitlements/restrictions errors
 *
 * **MediaKind TV Platform (TVP) Backend errors**
 *
 * These error codes are typically surfaced when something goes wrong during the SDK and TVP Backend communication (via REST API).
 *
 * These errors can be expected during playback of {@link MKRegisteredSource} content.
 *
 * The errors surfaced for this category have the error code format **4-6X-[code]**
 * - Where X can be a number between [0..9]
 * - [code] is the error code
 *
 * **Player errors**
 *
 * These errors codes are typically sufraced when something goes wrong during player setup, drm setup, network error when downloading content or any other playback related error.
 *
 * These errros can be expected for all source types.
 *
 * The errors surfaced for this category have the error code format **4-7x-[code]**
 * - Where X can be a number between [0..9]
 * - [code] is the error code
 *
 * **Program entitlement/restrictions errors**
 *
 * These error codes are typically surfaced in an event when the source content being played as entitlements/restrictions set to prevent playback from starting.
 *
 * These errors can be expected during playback of {@link MKRegisteredSource} content.
 *
 * The error codes for this category have the error code format **4-80-[code]**
 * - Where X can be a number between [0..9]
 * - [code] is the error code
 */
export enum MKErrorCode {
    /**
     * Typically caused due to and error with one or more input parameters to the TVP Backend request or due to an internal error for which an appropriate error code and message could not be determined.
     */
    TVP_REGISTRATION_GENERAL_ERROR = "4-61-33",

    /**
     * Typically seen when the DeviceProfile header information passed to the TVP backend is not properly base64 encoded. Users of the MKPlayer SDK will typically not be able to see/reproduce this error as MKPlayer SDK ensures to pass the correct DeviceProfile header information to the TVP Backend.
     */
    TVP_REGISTRATION_INVALID_DEVICE_PROFILE = "4-61-42",

    /**
     * Typically seen when the DeviceProfile header is missing from the headers in the HTTP request. Users of the MKPlayer SDK will typically not be able to see/reproduce this error as MKPlayer SDK ensures to pass the correct DeviceProfile header information to the TVP Backend.
     */
    TVP_REGISTRATION_MISSING_DEVICE_PROFILE = "4-61-43",

    /**
     * The provided STS token is either missing or invalid.
     */
    TVP_REGISTRATION_INVALID_STS_TOKEN = "4-61-3003",

    /**
     * HTTP 4xx or 5xx error that occur during TVP requests.
     */
    TVP_REGISTRATION_HTTP_ERROR = "4-61-125",

    /**
     * Typically caused due to and error with one or more input parameters to the TVP Backend request or due to an internal error for which an appropriate error code and message could not be determined.
     */
    TVP_ROLL_GENERAL_ERROR = "4-62-33",

    /**
     * Typically seen when the DeviceProfile header information passed to the TVP backend is not properly base64 encoded. Users of the MKPlayer SDK will typically not be able to see/reproduce this error as MKPlayer SDK ensures to pass the correct DeviceProfile header information to the TVP Backend.
     */
    TVP_ROLL_INVALID_DEVICE_PROFILE = "4-62-42",

    /**
     * Typically seen when the DeviceProfile header is missing from the headers in the HTTP request. Users of the MKPlayer SDK will typically not be able to see/reproduce this error as MKPlayer SDK ensures to pass the correct DeviceProfile header information to the TVP Backend.
     */
    TVP_ROLL_MISSING_DEVICE_PROFILE = "4-62-43",

    /**
     * No {@link MKDrmConfig} for this Platform. Typically seen when MF AV Backend is not configured for the new platform
     */
    TVP_ROLL_HTTP_DRM_ERROR = "4-62-125",

    /**
     * Typically seen when no mediaId was specified in the source configuration.
     */
    TVP_ROLL_MISSING_MEDIA_ID = "4-62-400",

    /**
     * Typically seen when the specified mediaId in the source configuration is not valid.
     */
    TVP_ROLL_INVALID_MEDIA_ID = "4-62-411",

    /**
     * Typically seen when {@link MKRegisteredSource.environmentConfig} does not contain a valid ownerUid or when the applicationToken in the source configuration for Live source is missing.
     */
    TVP_ROLL_INVALID_OWNER_OR_APPLICATION_TOKEN = "4-62-505",

    /**
     * Typically seen when the roll request does not carry the sessionId URL query parameter.
     */
    TVP_ROLL_INVALID_SESSION_ID = "4-62-564",

    /**
     * Typically seen when the session concurrency limit is reached for the user account in use.
     */
    TVP_ROLL_MAX_SESSIONS_EXCEEDED = "4-62-2413",

    /**
     * The provided STS token is either missing or invalid.
     */
    TVP_ROLL_INVALID_STS_TOKEN = "4-62-3003",

    /**
     * HTTP 4xx or 5xx error that occur during TVP requests.
     */
    TVP_ROLL_HTTP_ERROR = "4-62-3067",

    /**
     * Typically seen for a Live source if the specified {@link MKRegisteredSource.applicationToken} is invalid.
     */
    TVP_ROLL_CHANNEL_NOT_IN_SERVICE_OR_CHANNEL_NOT_FOUND = "4-62-3097",

    /**
     * This content is not available in your current location. Occurs when a user attempts to play content from a geographic location where the content is not licensed for distribution.
     */
    TVP_ROLL_CONTENT_RESTRICTED_GEOLOCATION = "4-62-3312",

    /**
     * Playback is not allowed in your current location due to insufficient offer level. Triggered when geo-location checks deny playback during authorization, even if the content request itself is valid.
     */
    TVP_ROLL_PLAYBACK_DENIED_GEOLOCATION = "4-62-3318",

    /**
     * This content is only available in your home country or approved regions. Occurs when a user attempts playback outside their home country and the current location is not part of an approved content portability region.
     */
    TVP_ROLL_OUT_OF_COUNTRY_PLAYBACK_REGION = "4-62-7027",

    /**
     * You have reached the maximum viewing limit outside your home country. Occurs when a user exceeds the allowed duration or usage limit for content portability while outside their home country.
     */
    TVP_ROLL_OUT_OF_COUNTRY_PLAYBACK_LIMIT_EXCEEDED = "4-62-7028",

    /**
     * Max sessions for platform group exceeded. Typically seen with LIFO based stream limit mechanism.
     */
    TVP_BEACON_MAX_SESSION_PLATFORM_GROUP_EXCEED = "4-63-2413",

    /**
     * Typically seen when the old session is stopped during FIFO based stream limit mechanism.
     */
    TVP_BEACON_MAX_SESSION_EXCEED = "4-63-2430",

    /**
     * Offer level stream limit exceeded. Typically seen with LIFO based stream limit mechanism.
     */
    TVP_BEACON_MAX_SESSION_OFFER_EXCEED = "4-63-3098",

    /**
     * Typically seen when the {@link MKRegisteredSource.offerId} for the currently viewing source has expired and the user is no longer entitled to continue watching this source.
     */
    TVP_BEACON_TIME_LIMITED_OFFER_EXPIRY = "4-63-3736",

    /**
     * When a more specific error could not be determined.
     */
    PLAYER_UNKNOWN_ERROR = "4-70-1000",

    /**
     * The player API was accessed after the player instance has been destroyed by either calling {@link MKPlayerAPI.destroy} or after an {@link MKPlayerEvent.Destroy} event has been thrown.
     *
     * It is recommended to not access the player API after the {@link MKPlayerEvent.Destroy} event has been thrown in order to avoid this error.
     */
    PLAYER_API_NOT_AVAILABLE = "4-70-1001",

    /**
     * Verification of the player license key failed. A valid license must be provided in the Info.plist or manifest file of the application or in the {@link MKPlayerConfig.key}.
     */
    PLAYER_LICENSE_KEY_NOT_FOUND = "4-70-1002",

    /**
     * The license request failed or was denied by the server. One possible reason could be that the application identifier is not authorised for the specified player license in the Bitmovin dashboard.
     */
    PLAYER_LICENSE_AUTHENTICATION_FAILED = "4-70-1003",

    /**
     * This is usually caused by general setup error.
     */
    GENERAL_SETUP_EEROR = "4-70-1100",

    /**
     * An attempt was made to create a player instance with a container element that is not an instance of HTMLElement.
     *
     * Some common causes for this error are:
     *
     * - trying to create a player instance with a container element returned by `document.getElementById(<id>)`, but using an incorrect ID
     * - trying to create a player instance before the window has finished loading, therefore the container element is not yet an element of the DOM tree
     *
     * Please ensure that the container element is available before creating the player instance.
     */
    PLAYER_SETUP_NO_HTML_ELEMENT = "4-70-1101",

    /**
     * No {@link MKPlayerConfig} was provided when attempting to create a player instance.
     *
     * The {@link MKPlayerConfig} is used to set different configuration options. e.g. {@link MKPlayerConfig.key}, {@link MKPlayerConfig.buffer} or {@link MKPlayerConfig.playback} and is required when creating a player instance.
     *
     * Example:
     * ```
     * const config = {
     *     key: "<PLAYER_KEY>",
     *     playback: {
     *         muted: false,
     *         autoplay: false
     *     }
     * };
     * ```
     */
    PLAYER_SETUP_MISSING_CONFIGURATION = "4-70-1102",

    /**
     * The player license server did not grant playback for the given player key as specified in the {@link MKPlayerConfig}.
     *
     * The most common cause for this error is that an incorrect or non-existing player key was specified in the {@link MKPlayerConfig}.
     */
    PLAYER_SETUP_LICENSE_ERROR = "4-70-1103",

    /**
     * The player build is domain-locked and not authorized to play content on the current domain.
     *
     * For domain-locked players, a list of allowed domains is specified at build time, and the player is only allowed to play back on the specified domains or subdomains of them.
     *
     * Please make sure that the host name matches the specified domain or is a subdomain of it.
     */
    PLAYER_SETUP_MISSING_DOMAIN_LICENSE_ALLOWLIST = "4-70-1104",

    /**
     * The player is not allowed to play back content on the current domain.
     *
     * This error is usually caused by the domain not being added to the list of allowed domains in the dashboard and can usually be resolved by adding the current domain to it.
     */
    PLAYER_SETUP_MISSING_LICENSE_ALLOWLIST = "4-70-1105",

    /**
     * The license server URL provided in licenseServer does not match any of the available license server URLs.
     * 
     * If licenseServer is not set, the default Bitmovin license server is used. Please make sure that the specified license server URL is valid.
     */
    PLAYER_SETUP_INVALID_LICENSE_SERVER = "4-70-1106",
    
    /**
     * The impression server URL provided in impressionServer does not match any of the available impression server URLs.
     * 
     * If impressionServer is not set, the default Bitmovin impression server is used. Please make sure that the specified impression server URL is valid
     */
    PLAYER_SETUP_INVALID_IMPRESSION_SERVER = "4-70-1107",

    /**
     * Could not initialize the rendering engine, which is either using the Media Source Extensions if the {@link MKPlayerType.Html5} player type is used or the native video element playback capabilities if the {@link MKPlayerType.Native} player type is used.
     *
     * This error occurs when the platform does not support the {@link MKPlayerType} and {@link MKStreamType} for the provided {@link MKSourceConfig} on the current platform, and is therefore unable to instantiate a suitable rendering engine.
     *
     * It can also occur when no suitable rendering engine is available for the {@link MKPlayerType} and {@link MKStreamType} specified in the {@link MKPlaybackConfig.preferredTech}.
     *
     * Ensure the following to fix the issue:
     * - at least one supported stream type (DASH, HLS) is present in the {@link MKSourceConfig}
     * - {@link MKPlaybackConfig.preferredTech} is not set to an unsupported combination of {@link MKPlayerType} and {@link MKStreamType} for the current platform.
     */
    PLAYER_SETUP_NO_RENDERING_ENGINE = "4-70-1108",

    /**
     * This error occurs when the flash version does not support playback.
     */
    PLAYER_SETUP_FLASH_VERSION = "4-70-1109",

    /**
     * This error occurs when the Native Flash is not authorized by a valid Adobe token.
     */
    PLAYER_SETUP_NATIVE_FLASH = "4-70-1110",

    /**
     * This error occurs when Flash doesn\'t have sufficient resources.
     */
    PLAYER_SETUP_FLASH_RESOURCE = "4-70-1111",

    /**
     * This error occurs when Flash container API not available.
     */
    PLAYER_SETUP_FLASH_API = "4-70-1112",

    /**
     * This site has been loaded using "file" protocol which is not supported. Please host the page using a web server (using http or https).
     */
     PLAYER_SETUP_UNSUPPORTED_PROTOCOL = "4-70-1113",

    /**
     * This is usually caused by general source error.
     */
    PLAYER_SOURCE_ERROR = "4-71-1200",

    /**
     * The SourceConfig provided to the {@link MKPlayerAPI.load} method is invalid.
     *
     * Ensure that the {@link MKSourceConfig} contains at least one valid source URL for any of the supported stream types on the current platform.
     */
    PLAYER_SOURCE_INVALID = "4-71-1201",

    /**
     * The downloaded manifest is invalid.
     *
     * This error is fired when the downloaded manifest is not valid or could not be parsed. Different stream types might fail because of different validation issues in the manifest.
     */
    PLAYER_SOURCE_MANIFEST_INVALID = "4-71-1202",

    /**
     * The platform does not support any of the technologies needed to play back the provided source.
     *
     * The error is fired when there is no support for any of the stream types on the current platform for the provided {@link MKSourceConfig}.
     *
     * Different platforms support different {@link MKPlayerType} for a given {@link MKStreamType} in the {@link MKSourceConfig}.
     */
    PLAYER_SOURCE_NO_SUPPORTED_TECHNOLOGY = "4-71-1203",

    /**
     * The stream type is not supported.
     */
    PLAYER_SOURCE_STREAM_TYPE_NOT_SUPPORTED = "4-71-1204",

    /**
     * The Technology selected through the forceTechnology parameter, that was passed to the {@link MKPlayerAPI.load} method, is not supported on the current platform.
     */
    PLAYER_SOURCE_FORCED_TECHNOLOGY_NOT_SUPPORTED = "4-71-1205",

    /**
     * The current platform doesn't support any of the technologies needed to play back any of the sources provided in the {@link MKSourceConfig}.
     */
    PLAYER_SOURCE_NO_STREAM_FOUND_FOR_SUPPORTED_TECHNOLOGY = "4-71-1206",

    /**
     * The downloaded segment is empty.
     *
     * This error occurs whenever an empty MP4 init-segment was downloaded, which usually indicates that there is an underlying issue with the stream. It is therefore recommended to check the segments of the stream - there may be an issue on the encoding side that caused empty segments to be generated.
     */
    PLAYER_SOURCE_EMPTY_SEGMENT = "4-71-1207",

    /**
     * The manifest could not be loaded.
     *
     * The manifest network request failed, please check the network request's response for the failure reason. In case an HLS stream was being loaded, this error can occur when the master playlist request or any of the initial variant playlist requests failed.
     *
     * The detailed failure reason can be found in the network request's response.
     *
     * This error is only triggered after the manifest request has failed a certain number of retries, By default, manifest requests are retried 2 times.
     */
    PLAYER_SOURCE_COULD_NOT_LOAD_MANIFEST = "4-71-1208",

    /**
     * The specified progressive stream type is not supported, or the SourceElement used to load the source has reported an error.
     * 
     * This error is only fired when the native player is used with a progressive source. In this case, playback is handled natively by the video element, and this error is thrown when a stream error is encountered by the video element.
     * 
     * Some common root causes for this error are:
     * - the browser not being able to load the progressive source due to CORS restrictions
     * - the browser not supporting the codec and/or container combination used by the asset
     * 
     * Please check the logged error message for more detailed info on what caused the error to be thrown.
     */
    PLAYER_SOURCE_PROGRESSIVE_STREAM_ERROR = "4-71-1209",

    /**
     * An error was encountered while playing, or trying to play back an HLS stream.
     *
     * This error can occur while loading an HLS stream, usually while loading or parsing of the variant playlists. For example, this error can be thrown when all network requests for all variant playlists fail. In case parsing of any of the loaded playlists fails, this error will also be thrown.
     *
     * Furthermore, this error can also occur when an HLS source is played back with the native player and the video element throws a playback error. In this case a more detailed failure reason may be included in the error message, if exposed by the video element.
     *
     * Please check the error message in the event for the exact reason of the failure.
     */
    PLAYER_SOURCE_HLS_STREAM_ERROR = "4-71-1210",

    /**
     * The encryption method used by the source is not supported.
     *
     * Currently, this error is thrown when an attempt is made to play back a SAMPLE-AES encrypted stream.
     *
     * Consider using a different encryption method, if possible - for example AES-128.
     */
    PLAYER_SOURCE_ENCRYPTION_METHOD_NOT_SUPPORTED = "4-71-1211",

    /**
     * An error occurred when attempting playback for the given source and a more specific error code could not be determined.
     */
    PLAYER_GENERAL_SOURCE_ERROR = "4-71-2001",

    /**
     * This is usually caused by general playback error.
     */
    PLAYER_PLAYBACK_ERROR = "4-72-1300",

    /**
     * An error occurred while trying to demux or decode the content.
     *
     * This error can occur when the browser or device is unable to either demux or decode the content. This can have a multitude of reasons, but it is usually an indication of there either being an issue with the asset itself, or the player not properly supporting the provided stream.
     *
     * Furthermore, this error can be thrown whenever the video element throws a MEDIA_ERR_DECODE, which is also an indication of there being an underlying issue with the provided stream.
     */
    PLAYER_PLAYBACK_VIDEO_DECODING_ERROR = "4-72-1301",

    /**
     * This error occurs if Flash renderer has an error.
     */
    PLAYER_PLAYBACK_FLASH_RENDER_ERROR = "4-72-1302",

    /**
     * The transmuxer could not be initialized.
     * 
     * This error can occur when an error is encountered during transmuxer initialization. The transmuxer is used to convert HLS transport stream segments into fMP4 segments that can be played back using the HTML5 based player.
     * 
     * This error mainly occurs because of issues during the setup of the transmuxer, and it may indicate that some dependencies like WebWorkers, the BlobBuilder or Blobs are not supported by the platform.
     */
    PLAYER_PLAYBACK_HLS_COULD_NOT_LOAD_TRANSMUXER = "4-72-1304",

    /**
     * An error occurred when attempting playback for the given source and a more specific error code could not be determined. Possibly a decode or related error can cause this.
     */
    PLAYER_PLAYBACK_FAILED = "4-72-2101",

    /**
     * The connection failed because the device isn't connected to the internet.
     */
    PLAYER_GENERAL_NETWORK_ERROR = "4-73-1009",

    /**
     * Could be caused because of some HTTP request error or timeout.
     */
    PLAYER_GENERAL_NETWORK_ERROR_HTTP_REQUEST_ERROR_OR_TIMEOUT = "4-73-1200",

    /**
     * A general network error has occurred while downloading resource E.g.: the manifest or a segment of the stream.
     *
     * When a segment request fails, the player tries to load the same segment of different quality from a different CDN,
     * if provided. If all of the request fails, this error is thrown. For HLS streams, backup streams are used to get the
     * same segment from a different origin.
     *
     * For live streams, failed segment downloads are per default ignored and the player continues with the next segment
     * instead of throwing an error.
     *
     * Please look at the response of the failed network request to determine the exact reason of the failure.
     */
    NETWORK_ERROR = "4-73-1400",

    /**
     * The manifest download request timed out.
     *
     * This error occurs when the manifest request was not completed within the default timeout of 20 seconds, and the request was therefore aborted, which is usually an indication of a poor network connection.
     *
     * When the native player is used with an HLS source, the player expects playback to start within a default timeout of 10 seconds. Should playback not start within that time frame, this error is thrown as well.
     *
     * Please check the network conditions and ensure, that the manifest is available and can be loaded.
     */
    PLAYER_NETWORK_MANIFEST_DOWNLOAD_TIMEOUT = "4-73-1401",

    /**
     * The segment download timed out.
     *
     * This error occurs when the segment download does not complete within the default timeout of 20 seconds, and the request was therefore aborted, which is usually an indication of a poor network connection.
     *
     * When a segment download fails due to a timeout, the player will attempt to load the same segment again from a different CDN/origin. This error is not thrown when segment downloads fail for DASH live streams, as segments that couldn't be downloaded are skipped without throwing an error in that case.
     *
     * Please check the network conditions and ensure, that the segments are available and can be loaded.
     */
    PLAYER_NETWORK_SEGMENT_DOWNLOAD_TIMEOUT = "4-73-1402",

    /**
     * The progressive stream download timed out.
     * 
     * This error occurs when loading of a progressive source doesn't complete within the default timeout of 20 seconds. As the native player is used for progressive sources, the player expects playback to start within a default timeout of 10 seconds. Should playback not start within that time frame, this error is thrown as well.
     * 
     * Please check the network conditions and ensure, that the progressive source is available and can be loaded.
     */
    PLAYER_NETWORK_PROGRESSIVE_STREAM_DOWNLOAD_TIMEOUT = "4-73-1403",

    /**
     * The Fairplay DRM certificate request failed.
     *
     * This error occurs when the Fairplay certificate could not be loaded due to a network error. Please check the network response in the error to determine the exact failure reason.
     *
     * Per default, the certificate request is retried once. The number of retries can be set using {@link MKFairplayDrmConfig.maxCertificateRequestRetries}
     */
    PLAYER_NETWORK_FAILED_CERTIFICATE_REQUEST = "4-73-1404",

    /**
     * Failed to download the source manifest.
     */
    PLAYER_MANIFEST_DOWNLOAD_FAILED = "4-73-2202",

    /**
     * This is usually caused by general DRM error.
     */
    PLAYER_DRM_ERROR = "4-74-2000",

    /**
     * When no DRM configuration was specified to playback a DRM protected source.
     */
    PLAYER_DRM_MISSING_CONFIGURATION = "4-74-2001",

    /**
     * No license server URL (LA_URL) was specified to playback the DRM protected content.
     */
    PLAYER_DRM_NO_LICENSE_SERVER_URL_PROVIDED = "4-74-2002",

    /**
     * The DRM License request failed.
     *
     * This error occurs when a DRM license request fails and the player could not retrieve a valid license from the license server. It can occur for a multitude of different reasons, but the most common ones include:
     * - the license server rejecting the license request, possible due to a misconfiguration or missing/expired tokens
     * - an incorrect DRM configuration being used, especially on smart TVs or set-top boxes, and preparing of the license request therefore failing
     *
     * Troubleshooting steps for license requests rejected by the DRM license server include:
     * - making sure that the license server is set up properly, e.g. that CORS allows license requests from different domains
     * - inspecting the detailed error message which is usually returned by the license server in the body of the license response
     * - ensuring, that all required HttpHeaders are set in the {@link MKDrmConfig} and e.g. tokens are valid and not expired
     *
     * Some Smart TVs also need special configuration options for PlayReady protected content playback. For example, most platforms required both {@link MKPlayReadyDrmConfig.plaintextChallenge} and {@link MKPlayReadyDrmConfig.utf8message} to be set, along with Content-Type {@link MKPlayReadyDrmConfig.headers}. If those options are not set, DRM license requests may fail.
     *
     * An example of such a {@link MKPlayReadyDrmConfig}, that needs to be used on most smart TVs, is shown below:
     * ```
     * {
     *     LA_URL: '<LA URL>',
     *     utf8message: true,
     *     plaintextChallenge: true,
     *     headers: {
     *         'Content-Type': 'text/xml',
     *     }
     * }
     * ```
     */
    PLAYER_DRM_FAILED_LICENSE_REQUEST = "4-74-2003",

    /**
     * Key or KeyId is missing
     */
    PLAYER_DRM_KEYID_MISSING = "4-74-2004",

    /**
     * Key size is not supported.
     */
    PLAYER_DRM_KEY_SIZE_NOT_SUPPORTED = "4-74-2005",

    /**
     * The current platform doesn't support any of the key systems specified in the {@link MKSourceConfig} that are required to play back the content.
     *
     * Different platforms support different DRM key systems, and when multiple key system configurations are provided in the {@link MKSourceConfig.drm}, the first supported one is chosen. If, however, the current platform doesn't support any of the key systems provided in the source config, this error will be thrown.
     *
     * In order to support DRM protected content playback on most platforms it is recommended to supply at least a {@link MKPlayReadyDrmConfig} and a {@link MKWidevineModularDrmConfig} for DRM protected sources.
     */
    PLAYER_DRM_NO_KEY_SYSTEM = "4-74-2006",

    /**
     * Unable to create or initialize a DRM key session.
     *
     * This error might occur during the initialization of a DRM Media Key Session which is used to both start DRM license requests as well as providing the Content Decryption Module with the licenses returned by the license server.
     *
     * As the DRM init data is used to generate Media Key Sessions, this error can be an indication of the init data being invalid or not supported on the current platform.
     *
     * Please verify, that the init data provided to the player is valid.
     */
    PLAYER_DRM_KEY_SESSION_INITIALIZATION_FAILED = "4-74-2007",

    /**
     * The DRM Media Keys object could not be created or initialized.
     *
     * The Media Keys object is associated to the video element and used to create Media Key Sessions, which are used to interface with the Content Decryption Module. This error might occur if the creation of the Media Keys, or attaching them the video element failed, which can happen because the key system is not supported by the platform or the data provided to create the instance is not valid.
     *
     * Please check the logs to determine the exact reason of the failure.
     */
    PLAYER_DRM_MEDIA_KEY_INITIALIZATION_FAILED = "4-74-2008",

    /**
     * The video element or an associated DRM Key Session has thrown a DRM key error.
     *
     * This error is triggered by the key session or the video element when a DRM error is encountered during playback. There are multiple different possible causes for this error, but the most common ones are:
     *
     * - the device/browser being unable to generate a DRM license request for the init data provided in the content
     * - the init data being malformed
     * - the Content Decryption Module not being able to parse/process the DRM license returned by the license server
     * - the Content Decryption Module encountering a general DRM related error during playback
     *
     * On certain platforms, a detailed error message is logged to the console, if one is available.
     */
    PLAYER_DRM_KEY_ERROR = "4-74-2009",

    /**
     * No supported Fairplay Key System is available.
     *
     * This error occurs when an attempt is made to play back a Fairplay protected stream on a platform that doesn't support any of the required Fairplay Key Systems.
     *
     * Please try to use a different DRM key system that is supported by the current platform. It is possible, and recommended, to define more than one key system configuration in the {@link MKDrmConfig}. The player will then choose the first key system, that is supported by the platform, and use it to play back the content.
     */
    PLAYER_DRM_KEY_SYSTEM_NOT_SUPPORTED = "4-74-2010",

    /**
     * The DRM certificate specified in the {@link MKDrmConfig} is not valid, the player was unable to parse the provided certificate or the network request for the certificate failed.
     *
     * This error can be thrown under the following conditions:
     *
     * - the Fairplay certificate request was not completed within the default network request timeout of 20 seconds
     * - preparing of a Fairplay license failed when calling {@link MKFairplayDrmConfig.prepareLicense} or {@link MKFairplayDrmConfig.prepareCertificate}
     * - the {@link MKFairplayDrmConfig.serverCertificate} is not an instance of an ArrayBuffer
     * - the provided {@link MKWidevineModularDrmConfig.serverCertificate} could not be set on the Media Keys
     *
     * Please verify that license and/or server certificate URLs are correct, and that the server certificate specified in the DRM config is valid, if one was specified.
     */
    PLAYER_DRM_CERTIFICATE_ERROR = "4-74-2011",

    /**
     * Invalid header key/value pair encountered while parsing a PlayReady license request.
     *
     * This error can occur, when the XML provided to the player through the DRM key-message event is malformed or contains invalid DRM request headers
     */
    PLAYER_DRM_PLAYREADY_INVALID_HEADER_KEY_VALUE_PAIR = "4-74-2012",

    /**
     * The DRM protected content cannot be played back due to the device not being able to securely present the content.
     *
     * This error can occur when an attempt is made to play back DRM protected content on a device or platform that is not capable of playing back the content in a secure manner. In most cases, this error occurs when a non-HDCP-capable monitor is connected to the system.
     *
     * The security level required to play back DRM protected content can also be configured on the DRM license server side. In order to lessen the required security level, and to e.g. allow content playback on platforms that for example don't support hardware decryption, please contact you DRM provider
     */
    PLAYER_DRM_RESTRICTED_OUTPUT = "4-74-2013",

    /**
     * This error IS DRM error for the Flash renderer.
     */
    PLAYER_DRM_FLASH_RENDER_ERROR = "4-74-2014",

    /**
     * No init data was found for the used key system in the manifest or the segments.
     *
     * In order to start a DRM license request, DRM init data is needed. This init data may be present in the manifest, the segments of the stream, or both. If no init data could be found in the manifest or the segments, the player will be unable to start a DRM license request and therefore throw this error.
     *
     * Please make sure that the DRM init data is present in the manifest and/or the segments of the stream.
     */
    PLAYER_DRM_INIT_DATA_MISSING = "4-74-2015",

    /**
     * General DRM error. A more specific error could not be determined.
     */
    PLAYER_GENERAL_DRM_ERROR = "4-74-2301",

    /**
     * The specified source configuration does not contain a valid DRM configuration.
     */
    PLAYER_DRM_CONFIGURATION_MISSING = "4-74-2302",

    /**
     * The DRM License Server URL is missing or is invalid.
     */
    PLAYER_NO_DRM_LICENSE_SERVER_URL_PROVIDED = "4-74-2303",

    /**
     * The DRM license request failed.
     */
    PLAYER_DRM_LICENSE_REQUEST_FAILED = "4-74-2306",

    /**
     * This is usually caused by general VR error.
     */
    PLAYER_VR_ERROR = "4-75-2100",

    /**
     * A more specific error could not be determined.
     */
    PLAYER_GENERAL_ERROR = "4-75-3001",

    /**
     * When the content download fails.
     */
    PLAYER_DOWNLOAD_FAILED = "4-75-3002",

    /**
     * There is not enough disk space left to proceed with downloading content.
     */
    PLAYER_INSUFFICIENT_STORAGE = "4-75-3003",

    /**
     * Failed to obtain a persist able content key for the DRM license.
     */
    PLAYER_FAILED_PERSISTING_DRM_LICENSE = "4-75-3004",

    /**
     * The user {@link MKSourceConfig} is not supported for download.
     */
    PLAYER_UNSUPPORTED_SOURCE_CONFIGURATION = "4-75-3009",

    /**
     * Content URL migration failed.
     */
    PLAYER_CONTENT_URL_MIGRATION_FAILED = "4-75-3010",

    /**
     * No relevant PSSH data was found for the used key system.
     */
    PLAYER_SEGMENT_PSSH_DATA_MISSING = "4-75-4000",

    /**
     * The used player technology not compatible with VR playback.
     * 
     * This error can occur when the loaded SourceConfig contains a VRConfig, but the current platform is not capable of playing back VR content with the chosen player technology. Depending on the platform, some StreamType are not supported for VR playback with some of the PlayerType. For example, VR playback is not supported with DASH sources in Safari.
     * 
     * Please make sure that current platform is capable of playing back the provided VR source.
     */
    PLAYER_VR_INCOMPATIBLE_PLAYER_TECHNOLOGY = "4-76-2101",

    /**
     * This is usually caused by general module error.
     */
    PLAYER_MODULE_ERROR = "4-76-3000",

    /**
     * The definition of the module is invalid (e.g. incomplete).
     * 
     * This error is triggered when addModule is called with an invalid module definition, for example when the name or the module property is missing.
     * 
     * Make sure the custom module definition is valid and follows the specification.
     */
    PLAYER_MODULE_INVALID_DEFINITION = "4-76-3001",

    /**
     * The module definition specifies dependencies but the module is not provided via a function for deferred loading.
     * 
     * This error is triggered when addModule is called with a custom module that defines dependencies, but the module is exported directly rather than through a function that can be invoked at a later point in time.
     * 
     * Make sure the custom module definition is valid and follows the specification.
     */
    PLAYER_MODULE_INVALID_DEFINITION_DEPENDENCY = "4-76-3002",

    /**
     * A module cannot be loaded because it has not been added to the player core.
     * 
     * This error occurs when a feature is used that needs a specific module to be loaded, but that module has not been added to the player. For example, when playing back a WebM stream, the ContainerWebM module is needed. Should that module not be present, this error will be thrown.
     * 
     * Please make sure all the required modules are loaded. Refer to ModuleName to determine, which modules are needed to supported the desired features.
     */
    PLAYER_MODULE_MISSING = "4-76-3003",

    /**
     * A module cannot be loaded because one or more dependencies are missing.
     * 
     * This error occurs when a module, that has dependencies on other modules, is added without adding all of its dependencies first.
     * 
     * Refer to ModuleName to determine the dependencies of the modules you're trying to add and ensure, that all dependencies are added before adding the module that depends on them.
     */
    PLAYER_MODULE_DEPENDENCY_MISSING = "4-76-3004",

    /**
     * A module has been removed from the player core. The error is triggered when a module is removed when there are active player instances.
     * 
     * This error occurs when a module is removed from the player core using removeModule.
     */
    PLAYER_MODULE_REMOVED = "4-76-3005",

    /**
     * An advertising module error has occurred. Refer to the attached AdvertisingError.
     * 
     * This error is triggered when an error happens during a client side ad break playback. The code contains a VastErrorCode and can be used to identify the exact error.
     */
    PLAYER_MODULE_ADVERTISING_ERROR = "4-77-3100",

    /**
     * Playback is not allowed over WiFi networks.
     */
    PROGRAM_RESTRICTION_WIFI = "4-80-100",

    /**
     * Playback is not allowed over Cellular networks.
     */
    PROGRAM_RESTRICTION_CELLULAR = "4-80-101",

    /**
     * Playback is not allowed on phone devices.
     */
    PROGRAM_RESTRICTION_PHONE = "4-80-102",

    /**
     * Playback is not allowed on tablet devices.
     */
    PROGRAM_RESTRICTION_TABLET = "4-80-103",

    /**
     * Playback is not allowed on STB devices.
     */
    PROGRAM_RESTRICTION_STB = "4-80-104",

    /**
     * Playback is not allowed on Jailbroken devices.
     */
    PROGRAM_RESTRICTION_JAILBROKEN = "4-80-105",

    /**
     * Playback is not allowed when out of home network coverage.
     */
    PROGRAM_RESTRICTION_OUT_OF_HOME = "4-80-106",

    /**
     * Playback is not allowed on iOS devices.
     */
    PROGRAM_RESTRICTION_IOS = "4-80-107",

    /**
     * Playback is not allowed on android devices.
     */
    PROGRAM_RESTRICTION_ANDROID = "4-80-108",

    /**
     * Playback is not allowed on desktop devices.
     */
    PROGRAM_RESTRICTION_DESKTOP = "4-80-109",

    /**
     * Rewind is not allowed.
     */
    REWIND_BLOCKED = "4-80-200",

    /**
     * FastForward is not allowed.
     */
    FASTFORWARD_BLOCKED = "4-80-201",

    /**
     * Pause is not allowed.
     */
    PAUSE_BLOCKED = "4-80-202",
    
    /**
     * Resume is not allowed.
     */
    RESUME_BLOCKED = "4-80-203",

    /**
     * SeekForward is not allowed.
     */
    SEEKFORWARD_BLOCKED = "4-80-204",
    
    /**
     * SeekBackward is not allowed.
     */
    SEEKBACKWARD_BLOCKED = "4-80-205",

    /**
     * RESTART is not allowed.
     */
    RESTART_BLOCKED = "4-80-206",

    /**
     * TimeShift is not allowed.
     */
    TIMESHIFT_BLOCKED = "4-80-207",

    /**
     * SeekSkip is not allowed.
     */
    SEEKSKIP_BLOCKED = "4-80-208",

    /**
     * CatchUp is not allowed.
     */
    CATCHUP_BLOCKED = "4-80-209",

    /**
     * Playback is not allowed over extended display (Ex: via HDMI)
     */
    PROGRAM_RESTRICTION_EXTENDED_DISPLAY = "4-80-300",

    /**
     * Playback is not allowed for AirPlay.
     */
    PROGRAM_RESTRICTION_AIRPLAY = "4-80-301",

    /**
     * Playback is not allowed for casting.
     */
    PROGRAM_RESTRICTION_CAST = "4-80-302",
}